import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { TopBarInfo, useCompactWidth } from './TopBarInfo';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icon';
import { SearchSuggestionsList } from '../../components/panels/SearchSuggestionsList';
import type { ResultEntry } from '../../components/panels/searchResultsModel';
import type { SearchRequestState } from '../../hooks/searchRequestState';
import type { SmilesImportState } from '../../hooks/useSmilesImport';
import type { PubChemMatch } from '@peruselab/api-client/moleculeSearch';
import { AccountMenu } from './AccountMenu';
import { BrandLogo } from './BrandLogo';
import { HelpLink } from '../../components/HelpLink';
import { TenantSwitcher } from '../../tenancy/TenantSwitcher';

interface TopBarProps {
  readonly mode: SearchMode;
  readonly onModeChange: (mode: SearchMode) => void;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  // Up to 5 live suggestions (#463) -- Text+Drawing mode passes an empty array (no dropdown for
  // that mode; see the plan's scope note), so the combobox markup below stays mode-agnostic.
  readonly suggestions: readonly ResultEntry[];
  readonly onSelectSuggestion: (card: ResultEntry) => void;
  readonly onCommitSearch: () => void;
  // #465: a PubChem-sourced hit when the local corpus has none, only ever non-null in pure
  // 'text' mode (WorkspaceShell gates it exactly like `suggestions`). Joins the same contiguous
  // option-index space `suggestions` already occupies -- see `totalOptionCount` below -- rather
  // than being a second, disjoint widget.
  readonly pubchemFallback: PubChemMatch | null;
  readonly onSelectPubChem: (match: PubChemMatch) => void;
  // #485: only the state/message are read here (never `results`), so this stays untyped by
  // whatever result shape the caller's search produces.
  readonly searchStatus: SearchRequestState<unknown>;
  readonly onRetrySearch: () => void;
  // #485: selecting the PubChem row imports its SMILES (via useSmilesImport), which can fail --
  // e.g. PubChem's own SMILES for "Lutetium Lu 177 Dotatate" contains an [177Lu+3] ion, and Lu
  // isn't in this editor's supported element set (validateImportedAtom in smilesImport.ts throws
  // "Lu is not in the current editor element set."). That error previously only ever rendered in
  // the separate File-drawer SmilesImportPanel, invisible from here, so clicking a PubChem result
  // that fails to import looked exactly like nothing happening at all.
  readonly pubchemImportStatus: SmilesImportState;
  /** #807 R3 -- the drawing's formula, match count and Graph stats, shown beside the account. */
  readonly info?: ReactNode;
  /** The match count for the collapsed info button's badge. */
  readonly infoCount?: number | null;
  /** #1110 P7: the open structure problems, flagged on the collapsed info button. */
  readonly infoProblems?: number;
}

export type SearchMode = 'drawing' | 'text' | 'textDrawing';

// ADR 0007 P1/P7 — brand + nav and (P7) the global search field, its Mode selector, and the
// account menu. #807: the P8 File control moved into the paper toolbar (EditorActionBar). #463: Text/Text+Drawing search actually run now; `mode`/
// `query` are controlled by WorkspaceShell (the same place `search`/`panelTab` etc. already
// live) so the suggestion dropdown and the full results region (rendered elsewhere in the tree)
// can share state. #461 restyled the field into one translucent pill (icon + input + mode
// select) rather than two visually mismatched boxes.
export function TopBar({
  mode,
  onModeChange,
  query,
  onQueryChange,
  suggestions,
  onSelectSuggestion,
  onCommitSearch,
  pubchemFallback,
  onSelectPubChem,
  searchStatus,
  onRetrySearch,
  pubchemImportStatus,
  info,
  infoCount = null,
  infoProblems = 0
}: TopBarProps) {
  const { t } = useTranslation();
  const headerRef = useRef<HTMLElement | null>(null);
  const compact = useCompactWidth(headerRef);
  const searchInputId = useId();
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // The PubChem row, when present, is the one extra option after every local suggestion --
  // same id space (`${listboxId}-option-${index}`), just one more index to range over.
  const totalOptionCount = suggestions.length + (pubchemFallback ? 1 : 0);
  // #485: once a PubChem row has been clicked, its import status takes over this space -- the
  // suggestion list stays stale (still including the very row just clicked) and would otherwise
  // hide the fact that anything happened at all.
  const pubchemImporting = pubchemImportStatus.state === 'loading' || pubchemImportStatus.state === 'error';
  const showSuggestions = open && totalOptionCount > 0 && !pubchemImporting;
  // #485: when there's nothing to list yet, the dropdown previously just didn't open at all --
  // no spinner while a request was in flight, no message if it failed, indistinguishable from a
  // hang. Shown only in that gap (never alongside actual suggestions, which speak for themselves).
  const showStatus =
    open &&
    !pubchemImporting &&
    totalOptionCount === 0 &&
    (searchStatus.state === 'loading' || searchStatus.state === 'error');
  const showPubchemImportStatus = open && pubchemImporting;

  // Closes the dropdown once a clicked PubChem row's import actually lands -- an error stays
  // open (showPubchemImportStatus above) until the next keystroke resets it (WorkspaceShell).
  useEffect(() => {
    if (pubchemImportStatus.state === 'success') setOpen(false);
  }, [pubchemImportStatus]);

  // Reset on every keystroke (query change), not on `suggestions` identity: `suggestions` is
  // recomputed in WorkspaceShell whenever useMoleculeFormulas resolves a label asynchronously in
  // the background (same result set, new array reference) -- resetting on that too raced with
  // arrow-key navigation, intermittently undoing a keypress between two ArrowDown presses that
  // landed either side of a formula resolving. A query change is the only thing that should ever
  // invalidate "what's highlighted."
  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  const selectSuggestion = (card: ResultEntry) => {
    onSelectSuggestion(card);
    setOpen(false);
  };

  const selectPubChem = (match: PubChemMatch) => {
    // #485: doesn't close eagerly like selectSuggestion -- this kicks off an import that can
    // fail, and the dropdown needs to stay open to show that (see showPubchemImportStatus above,
    // and the effect that closes it once the import actually succeeds).
    onSelectPubChem(match);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (mode === 'drawing') return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) setOpen(true);
        setActiveIndex((current) => Math.min(current + 1, totalOptionCount - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((current) => (current <= 0 ? -1 : current - 1));
        break;
      case 'Enter': {
        event.preventDefault();
        const activeSuggestion = activeIndex >= 0 ? suggestions[activeIndex] : undefined;
        if (open && activeSuggestion) {
          selectSuggestion(activeSuggestion);
        } else if (open && activeIndex === suggestions.length && pubchemFallback) {
          selectPubChem(pubchemFallback);
        } else {
          setOpen(false);
          onCommitSearch();
        }
        break;
      }
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
        }
        break;
      default:
        break;
    }
  };

  return (
    <header ref={headerRef} className="workspace-topbar" data-testid="workspace-topbar">
      {/* #309: axe's page-has-heading-one -- the shell has no visible page title to promote, so
          this names the page for screen-reader/outline navigation without changing the design. */}
      <h1 className="visually-hidden">{t('workspace.brand')}</h1>
      <Link to="/" className="workspace-brand" aria-label={t('workspace.brand')}>
        <BrandLogo className="workspace-brand-mark" />
        <span aria-hidden="true">{t('workspace.brand')}</span>
      </Link>
      <nav className="workspace-nav" aria-label={t('appNav.label')}>
        <NavLink to="/" end>
          {t('appNav.profile')}
        </NavLink>
        <NavLink to="/draw" end>
          {t('appNav.draw')}
        </NavLink>
      </nav>

      <form
        className="workspace-search"
        role="search"
        data-testid="workspace-search"
        onSubmit={(event) => {
          event.preventDefault();
          if (mode !== 'drawing') onCommitSearch();
        }}
      >
        <Icon name="search" className="workspace-search-icon" />
        <label className="visually-hidden" htmlFor={searchInputId}>
          {t('workspace.search.label')}
        </label>
        <input
          id={searchInputId}
          type="search"
          disabled={mode === 'drawing'}
          value={mode === 'drawing' ? '' : query}
          placeholder={t(
            mode === 'drawing'
              ? 'workspace.search.drawingPlaceholder'
              : mode === 'text'
                ? 'workspace.search.textPlaceholder'
                : 'workspace.search.textDrawingPlaceholder'
          )}
          data-testid="workspace-search-input"
          role="combobox"
          aria-expanded={showSuggestions || showStatus || showPubchemImportStatus}
          aria-controls={listboxId}
          aria-activedescendant={showSuggestions && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          onChange={(event) => {
            onQueryChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (mode !== 'drawing' && query.trim() !== '') setOpen(true);
          }}
          onBlur={() => setOpen(false)}
          onKeyDown={onInputKeyDown}
        />
        <span className="visually-hidden" aria-live="polite">
          {showSuggestions ? t('workspace.search.suggestionCount', { count: totalOptionCount }) : ''}
        </span>
        <span className="workspace-search-divider" aria-hidden="true" />
        <label className="visually-hidden" htmlFor={`${searchInputId}-mode`}>
          {t('workspace.search.modeLabel')}
        </label>
        <select
          id={`${searchInputId}-mode`}
          className="workspace-search-mode"
          value={mode}
          onChange={(event) => onModeChange(event.target.value as SearchMode)}
          data-testid="workspace-search-mode"
        >
          <option value="drawing">{t('workspace.search.modes.drawing')}</option>
          <option value="text">{t('workspace.search.modes.text')}</option>
          <option value="textDrawing">{t('workspace.search.modes.textDrawing')}</option>
        </select>
        {showSuggestions ? (
          <SearchSuggestionsList
            listboxId={listboxId}
            listboxLabel={t('workspace.search.suggestionsLabel')}
            suggestions={suggestions}
            activeIndex={activeIndex}
            onSelect={selectSuggestion}
            onHover={setActiveIndex}
            pubchemFallback={pubchemFallback}
            onSelectPubChem={selectPubChem}
          />
        ) : showPubchemImportStatus ? (
          <output className="workspace-search-suggestions workspace-search-status" aria-live="polite">
            {pubchemImportStatus.state === 'loading' ? t('smilesImportPanel.importing') : null}
            {pubchemImportStatus.state === 'error'
              ? t('smilesImportPanel.importFailed', { message: pubchemImportStatus.message })
              : null}
          </output>
        ) : showStatus ? (
          <output className="workspace-search-suggestions workspace-search-status" aria-live="polite">
            {searchStatus.state === 'loading' ? t('searchResultsPanel.loading') : null}
            {searchStatus.state === 'error' ? (
              <>
                <span>{searchStatus.message}</span>{' '}
                <button
                  type="button"
                  className="link-button"
                  // Keeps focus (and the dropdown) on the input, same reason the option rows
                  // above prevent default on mousedown.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={onRetrySearch}
                >
                  {t('searchResultsPanel.retry')}
                </button>
              </>
            ) : null}
          </output>
        ) : null}
      </form>

      <div className="workspace-topbar-actions">
        {info ? (
          <TopBarInfo compact={compact} count={infoCount} problems={infoProblems}>
            {info}
          </TopBarInfo>
        ) : null}
        <HelpLink />
        <TenantSwitcher />
        <AccountMenu />
      </div>
    </header>
  );
}
